import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RenewalHelpCard from '../components/RenewalHelpCard';
import DisclaimerNote from '../components/DisclaimerNote';
import { titleAccentDot } from '../utils/titleAccentDot';
import { useRenewalClick } from '../context/RenewalClickContext';
import { SCREENS, COVER_TYPES, RenewalJourneyEvent, RenewalJourneyLocation } from '../constants/renewalClick';
import {
  fireRenewalJourneyActivity,
} from '../api/renewalClickApi';
import RenewalOfferCard from '../components/RenewalOfferCard';
import PolicyDetailsAccordion from '../components/PolicyDetailsAccordion';
import {buildPolicyDetailRows} from '../mappers/renewalPolicyDetails.mapper';

const formatCurrency = (amount) => `₪${Number(amount).toLocaleString('he-IL')}`;

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const styles = {
  greeting: (theme) => ({
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 800,
    color: 'secondary.main',
    textAlign: 'left',
    mt: 2,
    mb: 1,
    '&::after': titleAccentDot,
  }),
  intro: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    color: 'text.secondary',
    textAlign: 'left',
    lineHeight: 1.6,
    mb: 0.75,
  }),
  introStrong: {
    fontWeight: 'bold',
    color: 'secondary.main',
  },
  sameTerms: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    color: 'text.secondary',
    textAlign: 'left',
    mb: 2.5,
  }),
  cta: (theme) => ({
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(16),
    py: 1.5,
    borderRadius: '24px',
    width: '100%',
  }),
  securePayment: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    mt: 1.25,
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
  }),
};

const OfferScreen = () => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({ id }, values);
  const {
    customer,
    vehicle,
    policy,
    pricing,
    opportunityId,
    setScreen,
  } = useRenewalClick();

  const handleContinue = useCallback(() => {
    if (opportunityId) {
      fireRenewalJourneyActivity(
        opportunityId,
        RenewalJourneyEvent.OFFER_CONFIRMED,
        { location: RenewalJourneyLocation.OFFER },
      );
    }
    setScreen(SCREENS.PAYMENT);
  }, [opportunityId, setScreen]);

  const handleDetailsExpand = useCallback(() => {
    if (opportunityId) {
      fireRenewalJourneyActivity(
        opportunityId,
        RenewalJourneyEvent.OFFER_DETAILS_OPENED,
        { location: RenewalJourneyLocation.OFFER },
      );
    }
  }, [opportunityId]);

  const offerLabels = {
    savingsLabel: m('renewalClick.offer.savingsLabel'),
    savingsCompare: m('renewalClick.offer.savingsCompare'),
    less: m('renewalClick.offer.less'),
    specialPrice: m('renewalClick.offer.specialPrice'),
    perYear: m('renewalClick.offer.perYear'),
    lastYearPrice: m('renewalClick.offer.lastYearPrice'),
    savingsBadge: m('renewalClick.offer.savingsBadge', {
      amount: formatCurrency(pricing.savings),
    }),
    breakdownMandatory: m('renewalClick.offer.breakdownMandatory', {
      amount: formatCurrency(pricing.mandatoryPremium),
    }),
    breakdownCasco: m(
      policy.coverType === COVER_TYPES.THIRD_PARTY
        ? 'renewalClick.offer.breakdownThirdParty'
        : 'renewalClick.offer.breakdownCasco',
      {amount: formatCurrency(pricing.cascoPremium)},
    ),
    breakdownServiceFee:
      pricing.serviceFee > 0
        ? m('renewalClick.offer.breakdownServiceFee', {
            amount: formatCurrency(pricing.serviceFee),
          })
        : undefined,
    breakdownOtherProducts: (pricing.otherProducts || []).map((p) =>
      m('renewalClick.offer.breakdownProduct', {name: p.name, amount: formatCurrency(p.amount)}),
    ),
    serviceFeeDisclaimer: m('renewalClick.offer.serviceFeeDisclaimer'),
  };

  const introValues = {
    coverType: m(
      policy.coverType === COVER_TYPES.THIRD_PARTY
        ? 'renewalClick.offer.introCoverThirdParty'
        : 'renewalClick.offer.introCoverComprehensive',
    ),
    manufacturer: vehicle.manufacturer || vehicle.model || m('renewalClick.offer.introVehicleFallback'),
    licensePlate: vehicle.licensePlate,
    endDate: formatDate(policy.endDate),
    b: (chunks) => <Box component='strong' sx={styles.introStrong}>{chunks}</Box>,
  };

  const detailRows = buildPolicyDetailRows({customer, vehicle, policy, pricing}, m);

  return (
    <Box sx={{ px: 2, pb: 5, maxWidth: 560, mx: 'auto' }}>
      {/* Greeting */}
      <Typography sx={styles.greeting}>
        {m('renewalClick.offer.greeting', { name: customer.name })}
      </Typography>
      <Typography sx={styles.intro}>
        <FormattedMessage id='renewalClick.offer.intro' values={introValues} />
      </Typography>
      <Typography sx={styles.sameTerms}>
        {m('renewalClick.offer.sameTerms')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <RenewalOfferCard offerData={pricing} policy={policy} labels={offerLabels} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <PolicyDetailsAccordion
          title={m('renewalClick.details.title')}
          rows={detailRows}
          onFirstExpand={handleDetailsExpand}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <RenewalHelpCard
          title={m('renewalClick.helpTitle')}
          description={m('renewalClick.helpDescription')}
        />
      </Box>

      {/* Disclaimer */}
      <DisclaimerNote
        icon={
          <InfoOutlinedIcon
            sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0, mt: 0.25 }}
          />
        }
        title={m('renewalClick.offer.disclaimerTitle')}
      >
        {m('renewalClick.offer.disclaimerText')}
      </DisclaimerNote>

      {/* CTA */}
      <Button
        variant='contained'
        color='primary'
        disabled={false}
        onClick={handleContinue}
        sx={styles.cta}
      >
        {m('renewalClick.offer.cta')} ←
      </Button>
      <Typography sx={styles.securePayment}>
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
        {m('renewalClick.securePayment')}
      </Typography>

      {process.env.REACT_APP_NODE_ENV === 'local' && (
        <button onClick={() => setScreen(SCREENS.SIGNING_PENDING)} style={{marginTop: 16}}>
          [DEV] Skip to signing
        </button>
      )}
    </Box>
  );
};

export default OfferScreen;
